import { useState } from 'react';
import {
    Box,
    Button,
    Card,
    CardContent,
    CircularProgress,
    Divider,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import {
    ShowBase,
    useDataProvider,
    useNotify,
    useRefresh,
    useShowContext,
    useUpdate,
} from 'react-admin';
import type { CrmDataProvider } from '@/providers/types';

import { formatRelativeBackendDate } from '@/utils/date_utils';
import { ticketStatuses } from '@/root/defaultConfiguration';
import { Ticket } from '@/types';
import {
    showMainSx,
    showPageRowSx,
} from '@/components/layout/showPageLayout';
import { TicketAside } from './TicketAside';

export const TicketShow = () => (
    <ShowBase queryOptions={{ meta: { includes: 'ticket_replies' } }}>
        <TicketShowContent />
    </ShowBase>
);

const TicketShowContent = () => {
    const { record, isPending } = useShowContext<Ticket>();
    const notify = useNotify();
    const refresh = useRefresh();
    const dataProvider = useDataProvider<CrmDataProvider>();
    const [update, { isPending: isUpdating }] = useUpdate();
    const [reply, setReply] = useState('');
    const [isReplying, setIsReplying] = useState(false);

    if (isPending || !record) return null;

    const isResolved = record.status === ticketStatuses.resolved;

    const handleResolve = () => {
        update(
            'tickets',
            {
                id: record.id,
                data: { status: ticketStatuses.resolved },
                previousData: record,
            },
            {
                onSuccess: () => {
                    notify('Ticket marked as resolved', { type: 'info' });
                    refresh();
                },
                onError: () => {
                    notify('Failed to update ticket', { type: 'warning' });
                },
            }
        );
    };

    const handleReply = async () => {
        const content = reply.trim();
        if (!content) return;
        setIsReplying(true);
        try {
            await dataProvider.createTicketReply(record.id, content);
            setReply('');
            notify('Reply added', { type: 'info' });
            refresh();
        } catch {
            notify('Failed to add reply', { type: 'warning' });
        } finally {
            setIsReplying(false);
        }
    };

    return (
        <Box sx={showPageRowSx}>
            <TicketAside />
            <Box sx={{ ...showMainSx, maxWidth: { md: 800 } }}>
                <Card>
                    <CardContent>
                        <Stack
                            direction={{ xs: 'column', sm: 'row' }}
                            justifyContent="space-between"
                            alignItems={{ xs: 'stretch', sm: 'flex-start' }}
                            gap={1.5}
                            mb={2}
                        >
                            <Box sx={{ minWidth: 0 }}>
                                <Typography variant="h5" mb={1}>
                                    Ticket #{record.id}
                                </Typography>
                                <Typography
                                    variant="body2"
                                    color="text.secondary"
                                >
                                    {record.created_by_name || 'Unknown'}
                                    {record.created_at
                                        ? ` · ${formatRelativeBackendDate(record.created_at)}`
                                        : ''}
                                </Typography>
                                <Typography
                                    variant="caption"
                                    color={
                                        isResolved
                                            ? 'success.main'
                                            : 'text.secondary'
                                    }
                                >
                                    {isResolved ? 'Resolved' : 'Pending'}
                                </Typography>
                            </Box>
                            {!isResolved && (
                                <Button
                                    variant="contained"
                                    color="success"
                                    onClick={handleResolve}
                                    disabled={isUpdating}
                                    sx={{
                                        alignSelf: { xs: 'stretch', sm: 'flex-start' },
                                        minHeight: 48,
                                    }}
                                >
                                    {isUpdating ? (
                                        <CircularProgress size={20} />
                                    ) : (
                                        'Mark resolved'
                                    )}
                                </Button>
                            )}
                        </Stack>
                        <Typography
                            variant="body1"
                            whiteSpace="pre-wrap"
                            sx={{ mb: 2, wordBreak: 'break-word' }}
                        >
                            {record.content}
                        </Typography>
                        <Divider sx={{ my: 2 }} />
                        <Typography variant="subtitle1" mb={1}>
                            Replies
                        </Typography>
                        {(record.replies ?? []).length === 0 ? (
                            <Typography
                                variant="body2"
                                color="text.secondary"
                                mb={2}
                            >
                                No replies yet
                            </Typography>
                        ) : (
                            <Stack spacing={1.5} mb={2}>
                                {(record.replies ?? []).map(item => (
                                    <Box
                                        key={item.id}
                                        sx={{
                                            p: 1.5,
                                            bgcolor: 'action.hover',
                                            borderRadius: 1,
                                        }}
                                    >
                                        <Typography
                                            variant="body2"
                                            whiteSpace="pre-wrap"
                                            sx={{ wordBreak: 'break-word' }}
                                        >
                                            {item.content}
                                        </Typography>
                                        <Typography
                                            variant="caption"
                                            color="text.secondary"
                                        >
                                            {item.created_by_name || 'Unknown'}
                                            {item.created_at
                                                ? ` · ${formatRelativeBackendDate(item.created_at)}`
                                                : ''}
                                        </Typography>
                                    </Box>
                                ))}
                            </Stack>
                        )}
                        <TextField
                            label="Add a reply"
                            value={reply}
                            onChange={e => setReply(e.target.value)}
                            fullWidth
                            multiline
                            minRows={3}
                            margin="dense"
                        />
                        <Box mt={1} display="flex" justifyContent="flex-end">
                            <Button
                                variant="contained"
                                onClick={handleReply}
                                disabled={isReplying || !reply.trim()}
                                sx={{ minHeight: 48 }}
                            >
                                {isReplying ? (
                                    <CircularProgress size={20} />
                                ) : (
                                    'Reply'
                                )}
                            </Button>
                        </Box>
                    </CardContent>
                </Card>
            </Box>
        </Box>
    );
};
